import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

export const ragQuery = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        nodes: z
          .array(
            z
              .object({
                content: z.string(),
                metadata: z.record(z.unknown()).nullable().optional(),
              })
              .passthrough(),
          )
          .min(1, "nodes array is required"),
        userQuery: z.string().optional(),
        language: z.string().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { nodes, userQuery, language } = data;

    const LOVABLE_API_KEY = process.env["LOVABLE_API_KEY"];
    if (!LOVABLE_API_KEY) throw new Error("LOVABLE_API_KEY not configured");

    const contextBlock = nodes
      .map(
        (n, i) =>
          `[Source ${i + 1}] (${(n.metadata?.["region"] as string) || "Africa"} | ${(n.metadata?.["category"] as string) || "general"})\n${n.content}`,
      )
      .join("\n\n");

    const isGerman = language === "de";

    const systemPrompt = `You are the Nearshore Africa Guide — an expert advisor for European companies exploring nearshore software development partnerships in Africa.

RULES:
- ONLY use information from the provided context. Do NOT hallucinate or invent facts.
- When discussing "Trust" or "Compliance", reference that Nearshore.Africa is operated by Evolvice GmbH (Stuttgart, Germany) which ensures German-standard quality management, GDPR compliance, and ISO-aligned processes.
- Highlight the Cairo/Egypt hub's timezone alignment (EET/UTC+2 = CET-1) as a key advantage for European businesses.
- Keep responses concise (2-3 sentences max), actionable, and business-focused.
- ${isGerman ? "Respond in German." : "Respond in English."}
- Format as a helpful insight card — no greeting, just the insight.

CONTEXT FROM KNOWLEDGE BASE:
${contextBlock}`;

    const response = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${LOVABLE_API_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "google/gemini-3-flash-preview",
        messages: [
          { role: "system", content: systemPrompt },
          {
            role: "user",
            content: userQuery || "What relevant insight can you share about this topic?",
          },
        ],
      }),
    });

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error("Rate limited, please try again later.");
      }
      if (response.status === 402) {
        throw new Error("AI credits exhausted.");
      }
      const errText = await response.text();
      console.error("RAG query error:", response.status, errText);
      throw new Error("AI query failed");
    }

    const responseData = await response.json();
    const answer: string = responseData.choices?.[0]?.message?.content || "";

    // Generate FAQ schema for AEO
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: userQuery || "Why nearshore to Africa?",
          acceptedAnswer: {
            "@type": "Answer",
            text: answer,
          },
        },
      ],
    };

    const result: { answer: string; faqSchema: Json; sources: Json[] } = {
      answer,
      faqSchema: faqSchema as Json,
      sources: nodes.map((n) => (n.metadata ?? null) as Json),
    };
    return result;
  });
